/**
 * Character Assets Management Utility
 * Handles mapping between character IDs, asset paths, and animation coordinates
 * Now uses AssetProvider pattern for future extensibility
 */

import { AssetProviderFactory, type CharacterAssetData } from './AssetProvider.js';

export interface CharacterInfo {
  id: string;
  displayName: string;
  description: string;
  assetPath: string;
}

export interface AnimationFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AnimationConfig {
  frames: AnimationFrame[];
  frameRate: number;
  loop: boolean;
}

export class CharacterAssets {
  private static readonly SPRITE_WIDTH = 16;
  private static readonly SPRITE_HEIGHT = 32;
  private static readonly FRAMES_PER_ROW = 56; // Correct spritesheet layout: 56 frames per row
  private static readonly PADDING = 1; // 1px padding between frames in padded spritesheets
  
  // Avatar sprite extraction constants
  private static readonly FRAME_INDEX = 3; // Static portrait frame (facing down)
  private static readonly FRAME_X_OFFSET = 0; // Offset to correct for padding layout
  private static readonly CROP_Y = 5; // Y position to start crop
  private static readonly CROP_WIDTH = 16; // Width of avatar crop
  private static readonly CROP_HEIGHT = 23; // Height of avatar crop
  
  // Asset provider for loading character assets (extensible)
  private static assetProvider = AssetProviderFactory.getProvider();

  // Character ID to asset path mapping (updated to use padded spritesheets)
  private static readonly CHARACTER_ASSETS: Record<string, string> = {
    'premade_01': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_01.png',
    'premade_02': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_02.png',
    'premade_03': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_03.png',
    'premade_04': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_04.png',
    'premade_05': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_05.png',
    'premade_06': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_06.png',
    'premade_07': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_07.png',
    'premade_08': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_08.png',
    'premade_09': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_09.png',
    'premade_10': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_10.png',
    'premade_11': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_11.png',
    'premade_12': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_12.png',
    'premade_13': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_13.png',
    'premade_14': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_14.png',
    'premade_15': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_15.png',
    'premade_16': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_16.png',
    'premade_17': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_17.png',
    'premade_18': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_18.png',
    'premade_19': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_19.png',
    'premade_20': 'characters/Character_Generator/0_Premade_Characters/16x16-padded/Premade_Character_20.png',
  };

  // Character display names and descriptions
  private static readonly CHARACTER_INFO: Record<string, { displayName: string; description: string }> = {
    'premade_01': { displayName: 'Adventurer', description: 'A brave explorer ready for any quest' },
    'premade_02': { displayName: 'Scholar', description: 'A wise academic with endless curiosity' },
    'premade_03': { displayName: 'Warrior', description: 'A fierce fighter with unmatched courage' },
    'premade_04': { displayName: 'Mage', description: 'A mystical spellcaster wielding arcane power' },
    'premade_05': { displayName: 'Ranger', description: 'A nature-loving scout with keen senses' },
    'premade_06': { displayName: 'Merchant', description: 'A shrewd trader with valuable connections' },
    'premade_07': { displayName: 'Artisan', description: 'A skilled craftsperson creating beautiful works' },
    'premade_08': { displayName: 'Guardian', description: 'A protective defender of the innocent' },
    'premade_09': { displayName: 'Healer', description: 'A compassionate caregiver who mends wounds' },
    'premade_10': { displayName: 'Rogue', description: 'A cunning shadow dancer with nimble fingers' },
    'premade_11': { displayName: 'Knight', description: 'An honorable champion of justice and valor' },
    'premade_12': { displayName: 'Bard', description: 'A charismatic storyteller spreading joy through song' },
    'premade_13': { displayName: 'Engineer', description: 'An inventive builder of mechanical marvels' },
    'premade_14': { displayName: 'Diplomat', description: 'A persuasive negotiator bridging differences' },
    'premade_15': { displayName: 'Mystic', description: 'A spiritual seeker attuned to hidden truths' },
    'premade_16': { displayName: 'Captain', description: 'A natural leader inspiring others to greatness' },
    'premade_17': { displayName: 'Alchemist', description: 'A curious experimenter mixing potions and elixirs' },
    'premade_18': { displayName: 'Navigator', description: 'A skilled wayfinder charting unknown territories' },
    'premade_19': { displayName: 'Sage', description: 'An enlightened teacher sharing ancient wisdom' },
    'premade_20': { displayName: 'Pioneer', description: 'A bold trailblazer venturing into the unknown' },
  };

  /**
   * Get all available characters
   */
  static getAllCharacters(): CharacterInfo[] {
    return Object.keys(CharacterAssets.CHARACTER_ASSETS).map(id => ({
      id,
      displayName: this.CHARACTER_INFO[id]?.displayName || 'Unknown Character',
      description: this.CHARACTER_INFO[id]?.description || 'No description available',
      assetPath: CharacterAssets.CHARACTER_ASSETS[id],
    }));
  }

  /**
   * Get character info by ID
   */
  static getCharacterInfo(characterId: string): CharacterInfo | null {
    const assetPath = CharacterAssets.CHARACTER_ASSETS[characterId];
    const info = this.CHARACTER_INFO[characterId];
    
    if (!assetPath || !info) {
      return null;
    }

    return {
      id: characterId,
      displayName: info.displayName,
      description: info.description,
      assetPath,
    };
  }

  /**
   * Get asset data for a character (async for future extensibility)
   */
  static async getCharacterAsset(characterId: string): Promise<CharacterAssetData | null> {
    try {
      return await this.assetProvider.getCharacterAsset(characterId);
    } catch (error) {
      console.warn('Failed to get character asset:', characterId, error);
      return null;
    }
  }

  /**
   * Get asset path for a character (restored for UI compatibility)
   */
  static getAssetPath(characterId: string): string | null {
    return CharacterAssets.CHARACTER_ASSETS[characterId] || null;
  }

  /**
   * Check if character ID is valid
   */
  static isValidCharacterId(characterId: string): boolean {
    return characterId in CharacterAssets.CHARACTER_ASSETS;
  }

  /**
   * Spritesheet frame index of the static, down-facing portrait pose.
   * Anything drawing a character as a still avatar should use this frame.
   */
  static getPortraitFrameIndex(): number {
    return CharacterAssets.FRAME_INDEX;
  }

  /** Width of one character spritesheet frame, in pixels. */
  static getSpriteWidth(): number {
    return CharacterAssets.SPRITE_WIDTH;
  }

  /** Height of one character spritesheet frame, in pixels. */
  static getSpriteHeight(): number {
    return CharacterAssets.SPRITE_HEIGHT;
  }

  /**
   * Get idle animation frame for avatar portrait
   * Uses internal sprite constants for avatar extraction
   */
  static getIdlePreviewFrame(characterId: string): AnimationFrame | null {
    if (!this.isValidCharacterId(characterId)) {
      return null;
    }

    // With padding: position = frame_index * (width + padding) + offset
    const frameX = this.FRAME_INDEX * (this.SPRITE_WIDTH + this.PADDING) + this.FRAME_X_OFFSET;
    const frameY = this.CROP_Y;

    return {
      x: frameX,
      y: frameY,
      width: this.CROP_WIDTH,
      height: this.CROP_HEIGHT
    };
  }

  /**
   * Get idle animation frames for a specific direction
   */
  static getIdleAnimation(direction: 'down' | 'left' | 'right' | 'up'): AnimationConfig {
    // IDLE animations are in Row 1 with direction order: right-up-left-down (6 frames each)
    const directionOffsets = {
      right: 56, // Row 1: frames 56-61 (positions 0-5)
      up: 62,    // Row 1: frames 62-67 (positions 6-11)
      left: 68,  // Row 1: frames 68-73 (positions 12-17)
      down: 74,  // Row 1: frames 74-79 (positions 18-23)
    };

    const startFrame = directionOffsets[direction];
    const frames: AnimationFrame[] = [];

    for (let i = 0; i < 6; i++) { // 6 frames per direction for idle
      const frameIndex = startFrame + i;
      const row = Math.floor(frameIndex / this.FRAMES_PER_ROW);
      const col = frameIndex % this.FRAMES_PER_ROW;

      frames.push({
        x: col * (this.SPRITE_WIDTH + this.PADDING), // Account for padding between frames
        y: row * (this.SPRITE_HEIGHT + this.PADDING), // Account for padding between rows
        width: this.SPRITE_WIDTH,
        height: this.SPRITE_HEIGHT,
      });
    }

    return {
      frames,
      frameRate: 8, // 8 FPS for idle animation
      loop: true,
    };
  }

  /**
   * Get running animation frames for a specific direction
   */
  static getRunAnimation(direction: 'down' | 'left' | 'right' | 'up'): AnimationConfig {
    // RUN animations are in Row 2 with direction order: right-up-left-down (6 frames each)
    const directionOffsets = {
      right: 112, // Row 2: frames 112-117 (positions 0-5)
      up: 118,    // Row 2: frames 118-123 (positions 6-11)
      left: 124,  // Row 2: frames 124-129 (positions 12-17)
      down: 130,  // Row 2: frames 130-135 (positions 18-23)
    };

    const startFrame = directionOffsets[direction];
    const frames: AnimationFrame[] = [];

    for (let i = 0; i < 6; i++) { // 6 frames per direction for run
      const frameIndex = startFrame + i;
      const row = Math.floor(frameIndex / this.FRAMES_PER_ROW);
      const col = frameIndex % this.FRAMES_PER_ROW;

      frames.push({
        x: col * (this.SPRITE_WIDTH + this.PADDING), // Account for padding between frames
        y: row * (this.SPRITE_HEIGHT + this.PADDING), // Account for padding between rows
        width: this.SPRITE_WIDTH,
        height: this.SPRITE_HEIGHT,
      });
    }

    return {
      frames,
      frameRate: 12, // 12 FPS for running animation
      loop: true,
    };
  }

  /**
   * Get default character ID
   */
  static getDefaultCharacterId(): string {
    return 'premade_01';
  }

  /**
   * Get character IDs in grid order for UI display (5x4 layout)
   */
  static getCharacterGrid(): string[][] {
    const characters = Object.keys(CharacterAssets.CHARACTER_ASSETS).sort();
    const grid: string[][] = [];
    
    for (let row = 0; row < 4; row++) {
      const rowCharacters: string[] = [];
      for (let col = 0; col < 5; col++) {
        const index = row * 5 + col;
        if (index < characters.length) {
          rowCharacters.push(characters[index]);
        }
      }
      if (rowCharacters.length > 0) {
        grid.push(rowCharacters);
      }
    }
    
    return grid;
  }

  /**
   * Generate character-specific sprite key for Phaser
   */
  static getCharacterSpriteKey(characterId: string, type: 'idle' | 'run' = 'idle'): string {
    // Format: character_type_direction (e.g., premade_01_idle, premade_01_run)
    return `${characterId}_${type}`;
  }

  /**
   * Generate character-specific animation key
   */
  static getCharacterAnimationKey(characterId: string, type: 'idle' | 'run' | 'sit', direction: 'down' | 'left' | 'right' | 'up'): string {
    // Format: character_type_direction (e.g., premade_01_idle_down, premade_01_run_right, premade_01_sit_left)
    return `${characterId}_${type}_${direction}`;
  }

  /**
   * Set asset provider (for dependency injection)
   */
  static setAssetProvider(provider: any): void {
    this.assetProvider = provider;
  }
}

export default CharacterAssets;